import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, {
  CodeBlock,
  Callout,
  pagerFor,
} from 'Containers/MCP/docs/DocsLayout';
import {
  MCP_ENDPOINT,
  TOOL_COUNT,
  RESOURCE_COUNT,
} from 'data/mcpTools';

const PATH = '/docs/mcp/clients';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#matrix', label: 'At a glance' },
  { href: '#claude-web', label: 'Claude on the web' },
  { href: '#claude-desktop', label: 'Claude Desktop' },
  { href: '#claude-code', label: 'Claude Code' },
  { href: '#cursor', label: 'Cursor' },
  { href: '#own-agent', label: 'Connect your own agent' },
  { href: '#spec', label: 'Connector specification' },
];

const CLIENTS = [
  {
    id: 'claude-web',
    name: 'Claude on the web',
    status: 'supported',
    label: 'Supported',
    summary: 'Add it under Settings → Connectors, then sign in.',
  },
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    status: 'supported',
    label: 'Supported',
    summary: 'Nothing to add — it inherits the connector from your Claude account.',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    status: 'soon',
    label: 'Coming soon',
    summary: 'Signs in over a local callback address the connector cannot accept yet.',
  },
  {
    id: 'cursor',
    name: 'Cursor',
    status: 'soon',
    label: 'Coming soon',
    summary: 'Its sign-in callback is not registered with the connector yet.',
  },
];

const Clients = () => (
  <DocsLayout
    eyebrow="Clients"
    title="Supported clients"
    lede={
      <>
        The connector is a remote MCP server, so any client that speaks MCP over
        HTTP and can complete an OAuth sign-in could in principle use it. In
        practice the sign-in is the gate: a client can only connect if the address
        it returns to has been registered with the connector. Two are cleared
        today.
      </>
    }
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <h2 id="matrix">At a glance</h2>
    <div className="mcp-docs__table-wrap">
      <table className="mcp-docs__table">
        <caption className="visually-hidden">
          Client support for the Vocable MCP connector
        </caption>
        <thead>
          <tr>
            <th scope="col">Client</th>
            <th scope="col">Status</th>
            <th scope="col">What you do</th>
          </tr>
        </thead>
        <tbody>
          {CLIENTS.map((client) => (
            <tr key={client.id}>
              <th scope="row">
                <a href={`#${client.id}`}>{client.name}</a>
              </th>
              <td>
                <span
                  className={`mcp-docs__badge mcp-docs__badge--${client.status}`}
                >
                  {client.label}
                </span>
              </td>
              <td>{client.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="claude-web">Claude on the web</h2>
    <p>
      The primary client, and where a custom connector is added in the first
      place. You need a Claude plan that includes custom connectors; the option
      does not appear otherwise.
    </p>
    <CodeBlock code={MCP_ENDPOINT} label="Connector URL" language="text" />
    <p>
      Open <strong>Settings &rarr; Connectors &rarr; Add custom connector</strong>
      , paste the URL, click <strong>Add</strong>, then <strong>Connect</strong>{' '}
      and sign in to Vocable.{' '}
      <Link to="/docs/mcp/quickstart">Connect the connector</Link> walks through
      it with the things that commonly trip people up.
    </p>

    <h2 id="claude-desktop">Claude Desktop</h2>
    <p>
      No separate setup. Custom connectors are held against your Claude account
      rather than per application, so a connector you added on the web is already
      listed in Claude Desktop when you are signed in to the same account. One
      connection, both surfaces.
    </p>
    <Callout tone="tip" title="Not seeing it?">
      <p>
        Check you are signed in to Claude Desktop with the same account you used
        on the web. If the connector is missing there, it is an account mismatch
        rather than a Vocable-side problem.
      </p>
    </Callout>

    <h2 id="claude-code">Claude Code</h2>
    <p>
      Not supported yet. Claude Code completes the OAuth sign-in on a local
      loopback address &mdash; something like{' '}
      <code className="mcp-docs__code-inline">http://localhost:PORT/callback</code>{' '}
      &mdash; and the connector only accepts the callback addresses registered
      with it, which do not include local ones. Authorisation therefore fails
      after you have already signed in, which is the worst place for it to fail.
      There is nothing to configure on your side to work around it.
    </p>

    <h2 id="cursor">Cursor</h2>
    <p>
      Not supported yet, for the same reason: the address Cursor returns to is
      not registered with the connector, so the sign-in cannot complete. Use
      Claude Web or Claude Desktop in the meantime.
    </p>

    <h2 id="own-agent">Connect your own agent</h2>
    <p>
      If you are building an agent and want it to reach Vocable, the connector
      itself is standard &mdash; the specification below is everything a client
      needs. The one thing you cannot do unilaterally is the sign-in: your
      agent&rsquo;s callback address has to be registered with the connector
      first, or the authorisation will be rejected at the last step.
    </p>
    <p>
      Email <a href="mailto:support@vocable.ai">support@vocable.ai</a> with the
      client you are building and the exact callback URL it uses, and we will
      tell you where it stands. If you are writing server-to-server code rather
      than an agent, the Vocable API is the better fit &mdash; see{' '}
      <Link to="/docs/mcp/overview">the overview</Link> for the distinction.
    </p>

    <h2 id="spec">Connector specification</h2>
    <div className="mcp-docs__table-wrap">
      <table className="mcp-docs__table">
        <caption className="visually-hidden">
          Technical specification of the remote Vocable MCP connector
        </caption>
        <tbody>
          <tr>
            <th scope="row">Endpoint</th>
            <td>
              <code className="mcp-docs__code-inline">{MCP_ENDPOINT}</code>
            </td>
          </tr>
          <tr>
            <th scope="row">Transport</th>
            <td>Streamable HTTP</td>
          </tr>
          <tr>
            <th scope="row">Authorisation</th>
            <td>
              OAuth 2.1 authorisation code with PKCE. Public client &mdash; there
              is no client secret to hold.
            </td>
          </tr>
          <tr>
            <th scope="row">Client registration</th>
            <td>
              Dynamic registration is supported, but the redirect URI still has
              to be one the connector accepts.
            </td>
          </tr>
          <tr>
            <th scope="row">Resource metadata</th>
            <td>
              <code className="mcp-docs__code-inline">
                /.well-known/oauth-protected-resource/mcp/v1
              </code>{' '}
              on the same host, as referenced by the{' '}
              <code className="mcp-docs__code-inline">WWW-Authenticate</code>{' '}
              challenge on a 401.
            </td>
          </tr>
          <tr>
            <th scope="row">Access token lifetime</th>
            <td>24 hours, after which the client re-authorises.</td>
          </tr>
          <tr>
            <th scope="row">Tools</th>
            <td>
              {TOOL_COUNT} &mdash; see the{' '}
              <Link to="/docs/mcp/tools">tool reference</Link>.
            </td>
          </tr>
          <tr>
            <th scope="row">Resources</th>
            <td>
              {RESOURCE_COUNT} read-only brand resources &mdash; see{' '}
              <Link to="/docs/mcp/resources">resources</Link>.
            </td>
          </tr>
          <tr>
            <th scope="row">Prompts</th>
            <td>None. The connector exposes tools and resources only.</td>
          </tr>
          <tr>
            <th scope="row">Scoping</th>
            <td>
              One brand at a time, chosen per connection. The token carries your
              own Vocable permissions and nothing more.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </DocsLayout>
);

export default Clients;
